import React from 'react';
import clsx from 'clsx';
import { PencilIcon } from '@heroicons/react/16/solid';

/**
 * A value edited where it is read. The input is always rendered, only its border and
 * fill change, and the action slot keeps its width, so editing shifts nothing.
 */
export function InlineEditValue(props: {
  /** Names the input for assistive technology. */
  label: string;
  /** Names the pencil; defaults to "Edit" and the label. */
  editLabel?: string;
  value: string;
  /** Shown in place of an empty value, in both modes. */
  placeholder?: string;
  editing: boolean;
  editable: boolean;
  onStartEditing: () => void;
  onChange: (next: string) => void;
  onDoneEditing: () => void;
  /** Takes the action slot while editing, in place of the pencil. */
  editingAction?: React.ReactNode;
  valueClassName?: string;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (props.editing) {
      inputRef.current?.focus();
    }
  }, [props.editing]);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <input
        ref={inputRef}
        aria-label={props.label}
        value={props.value}
        placeholder={props.placeholder}
        readOnly={!props.editing}
        tabIndex={props.editing ? 0 : -1}
        title={props.editing ? undefined : props.value}
        onChange={(event) => props.onChange(event.target.value)}
        onBlur={() => props.editing && props.onDoneEditing()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && props.editing) {
            props.onDoneEditing();
          }
        }}
        // The negative margin puts read-mode text on the column's edge; the box's
        // padding is only visible once its border is.
        className={clsx(
          '-ml-[11px] h-8 w-full min-w-0 truncate rounded-lg border px-[11px] text-sm/6 outline-none placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
          props.editing
            ? 'border-neutral-950/10 bg-white shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 dark:border-white/10 dark:bg-white/5'
            : 'cursor-default border-transparent bg-transparent',
          props.valueClassName ?? 'text-neutral-950 dark:text-white'
        )}
      />
      <span className="grid size-7 flex-none place-items-center">
        {props.editing
          ? props.editingAction
          : props.editable && (
              <button
                type="button"
                aria-label={props.editLabel ?? `Edit ${props.label}`}
                onClick={props.onStartEditing}
                className="rounded p-1 text-neutral-500 hover:bg-neutral-950/5 dark:text-neutral-400 dark:hover:bg-white/5"
              >
                <PencilIcon className="size-3.5" />
              </button>
            )}
      </span>
    </div>
  );
}
